import { AlertTriangle, Play, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DEFAULT_SCREEN_FILTERS,
  SCAN_WINDOWS,
  SCREEN_RESULT_LIMIT,
  ScreenFiltersSchema,
  type MarketScreenDto,
  type ScanWindow,
  type ScreenFilters,
} from 'shared';
import { toast } from 'sonner';

import { StockCandidateList } from '../components/domain/StockCandidateList';
import { Button } from '../components/ui/Button';
import { SymbolExport } from '../components/ui/SymbolExport';
import {
  useCreateScreen,
  useDeleteScreen,
  useUpdateScreen,
} from '../features/marketConsole/mutations';
import { useMarketScreens, useScreenResult } from '../features/marketConsole/queries';
import { useReplayDate } from '../lib/useReplayDate';

const MILLION = 1_000_000;

type NumberField =
  | 'minPerformancePct'
  | 'maxPerformancePct'
  | 'topPct'
  | 'minDollarVolumeM'
  | 'minAverageVolume10d'
  | 'minAdrPct'
  | 'maxAdrPct'
  | 'minAtrExtension'
  | 'maxAtrExtension'
  | 'minClose';

/** Screen filters in the units people type; blank numbers mean "no limit". */
interface Draft {
  window: ScanWindow;
  numbers: Record<NumberField, string>;
  aboveSma50: boolean;
  tightOnly: boolean;
  excludeBiotech: boolean;
  industries: string[];
  excludeIndustries: string[];
}

function text(value: number | undefined, scale = 1): string {
  return value === undefined ? '' : String(Math.round((value / scale) * 1000) / 1000);
}

function toDraft(filters: ScreenFilters): Draft {
  return {
    window: filters.window,
    numbers: {
      minPerformancePct: text(filters.minPerformancePct),
      maxPerformancePct: text(filters.maxPerformancePct),
      topPct: text(filters.topPct, 0.01),
      minDollarVolumeM: text(filters.minAverageDollarVolume30d, MILLION),
      minAverageVolume10d: text(filters.minAverageVolume10d),
      minAdrPct: text(filters.minAdrPct),
      maxAdrPct: text(filters.maxAdrPct),
      minAtrExtension: text(filters.minAtrExtension),
      maxAtrExtension: text(filters.maxAtrExtension),
      minClose: text(filters.minClose),
    },
    aboveSma50: filters.aboveSma50,
    tightOnly: filters.tightOnly,
    excludeBiotech: filters.excludeBiotech,
    industries: filters.industries,
    excludeIndustries: filters.excludeIndustries,
  };
}

function toFilters(draft: Draft): ScreenFilters | string {
  const number = (field: NumberField, scale = 1): number | undefined => {
    const raw = draft.numbers[field].trim();
    return raw === '' ? undefined : Number(raw) * scale;
  };
  const candidate = {
    window: draft.window,
    minPerformancePct: number('minPerformancePct'),
    maxPerformancePct: number('maxPerformancePct'),
    topPct: number('topPct', 0.01),
    minAverageDollarVolume30d: number('minDollarVolumeM', MILLION),
    minAverageVolume10d: number('minAverageVolume10d'),
    minAdrPct: number('minAdrPct'),
    maxAdrPct: number('maxAdrPct'),
    minAtrExtension: number('minAtrExtension'),
    maxAtrExtension: number('maxAtrExtension'),
    minClose: number('minClose'),
    aboveSma50: draft.aboveSma50,
    tightOnly: draft.tightOnly,
    excludeBiotech: draft.excludeBiotech,
    industries: draft.industries,
    excludeIndustries: draft.excludeIndustries,
  };
  const defined = Object.fromEntries(
    Object.entries(candidate).filter(([, value]) => value !== undefined),
  );
  const parsed = ScreenFiltersSchema.safeParse(defined);
  if (parsed.success) return parsed.data;
  const field = parsed.error.issues[0]?.path[0];
  return `Check ${typeof field === 'string' ? field : 'the filters'}: the value is not allowed.`;
}

const NUMBER_FIELDS: ReadonlyArray<{ field: NumberField; label: string; step: string }> = [
  { field: 'minPerformancePct', label: 'Min return %', step: 'any' },
  { field: 'maxPerformancePct', label: 'Max return %', step: 'any' },
  { field: 'topPct', label: 'Top % by return', step: 'any' },
  { field: 'minDollarVolumeM', label: 'Min 30D $ volume ($M)', step: 'any' },
  { field: 'minAverageVolume10d', label: 'Min 10D volume', step: '1' },
  { field: 'minAdrPct', label: 'Min ADR%', step: 'any' },
  { field: 'maxAdrPct', label: 'Max ADR%', step: 'any' },
  { field: 'minAtrExtension', label: 'Min ATRs over 50D', step: 'any' },
  { field: 'maxAtrExtension', label: 'Max ATRs over 50D', step: 'any' },
  { field: 'minClose', label: 'Min price', step: 'any' },
];

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function IndustryPicker({
  id,
  label,
  values,
  options,
  onChange,
}: {
  id: string;
  label: string;
  values: readonly string[];
  options: readonly string[];
  onChange: (values: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const add = () => {
    const value = input.trim();
    if (value && !values.includes(value)) onChange([...values, value]);
    setInput('');
  };
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-xs font-medium text-text-muted">
        {label}
      </label>
      <div className="flex gap-1">
        <input
          id={id}
          list={`${id}-options`}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
          placeholder="Type or pick"
          className="h-9 min-w-0 flex-1 rounded border border-border bg-bg px-2 text-sm text-text"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={`Add to ${label}`}
          onClick={add}
        >
          Add
        </Button>
      </div>
      <datalist id={`${id}-options`}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
      {values.length > 0 ? (
        <ul className="flex flex-wrap gap-1 pt-1">
          {values.map((value) => (
            <li
              key={value}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-xs"
            >
              {value}
              <button
                type="button"
                aria-label={`Remove ${value}`}
                onClick={() => onChange(values.filter((item) => item !== value))}
                className="text-text-muted hover:text-danger"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SavedScreens({
  screens,
  current,
  draft,
  onLoad,
  onError,
}: {
  screens: readonly MarketScreenDto[];
  current: MarketScreenDto | null;
  draft: Draft;
  onLoad: (screen: MarketScreenDto | null) => void;
  onError: (message: string) => void;
}) {
  const create = useCreateScreen();
  const update = useUpdateScreen();
  const remove = useDeleteScreen();
  const [name, setName] = useState('');
  const pending = create.isPending || update.isPending || remove.isPending;

  const filters = (): ScreenFilters | null => {
    const result = toFilters(draft);
    if (typeof result === 'string') {
      onError(result);
      return null;
    }
    return result;
  };

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="space-y-1">
        <label htmlFor="saved-screen" className="text-xs font-medium text-text-muted">
          Saved screen
        </label>
        <select
          id="saved-screen"
          value={current?.id ?? ''}
          onChange={(event) =>
            onLoad(screens.find((screen) => screen.id === event.target.value) ?? null)
          }
          className="h-9 rounded border border-border bg-bg px-2 text-sm text-text"
        >
          <option value="">New screen</option>
          {screens.map((screen) => (
            <option key={screen.id} value={screen.id}>
              {screen.name}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label htmlFor="screen-name" className="text-xs font-medium text-text-muted">
          Name
        </label>
        <input
          id="screen-name"
          value={name}
          maxLength={60}
          onChange={(event) => setName(event.target.value)}
          placeholder={current?.name ?? 'e.g. Tight semis'}
          className="h-9 w-44 rounded border border-border bg-bg px-2 text-sm text-text"
        />
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={pending || name.trim() === ''}
        onClick={() => {
          const value = filters();
          if (!value) return;
          create.mutate(
            { name: name.trim(), filters: value },
            {
              onSuccess: (screen) => {
                toast.success(`Saved "${screen.name}".`);
                setName('');
                onLoad(screen);
              },
              onError: (error) => toast.error(errorText(error, 'Could not save the screen.')),
            },
          );
        }}
      >
        Save as new
      </Button>
      {current ? (
        <>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={pending}
            onClick={() => {
              const value = filters();
              if (!value) return;
              update.mutate(
                { id: current.id, input: { name: name.trim() || current.name, filters: value } },
                {
                  onSuccess: (screen) => {
                    toast.success(`Updated "${screen.name}".`);
                    setName('');
                    onLoad(screen);
                  },
                  onError: (error) => toast.error(errorText(error, 'Could not update the screen.')),
                },
              );
            }}
          >
            Update
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={pending}
            onClick={() =>
              remove.mutate(current.id, {
                onSuccess: () => {
                  toast.success(`Deleted "${current.name}".`);
                  onLoad(null);
                },
                onError: (error) => toast.error(errorText(error, 'Could not delete the screen.')),
              })
            }
          >
            Delete
          </Button>
        </>
      ) : null}
    </div>
  );
}

export function MarketScreener() {
  const { asOf } = useReplayDate();
  const screens = useMarketScreens();
  const [draft, setDraft] = useState<Draft>(() => toDraft(DEFAULT_SCREEN_FILTERS));
  const [applied, setApplied] = useState<ScreenFilters>(DEFAULT_SCREEN_FILTERS);
  const [current, setCurrent] = useState<MarketScreenDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const result = useScreenResult(applied, asOf);

  const setNumber = (field: NumberField, value: string) =>
    setDraft((state) => ({ ...state, numbers: { ...state.numbers, [field]: value } }));

  const run = () => {
    const filters = toFilters(draft);
    if (typeof filters === 'string') {
      setError(filters);
      return;
    }
    setError(null);
    setApplied(filters);
  };

  const load = (screen: MarketScreenDto | null) => {
    setCurrent(screen);
    const filters = screen?.filters ?? DEFAULT_SCREEN_FILTERS;
    setDraft(toDraft(filters));
    setApplied(filters);
    setError(null);
  };

  const data = result.data;
  const industries = data?.industries ?? [];

  return (
    <main className="text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Screener</h1>
            <p className="mt-1 text-sm text-text-muted">
              Your own filters over every liquid US stock in the latest scan. Save screens to rerun
              them on any session. Industries include{' '}
              <Link to="/settings/stock-groups" className="underline">
                your own stock groups
              </Link>
              .
            </p>
          </div>
          {data?.sessionDate ? (
            <div className="text-xs text-text-muted">
              Close · {data.sessionDate} · Informational only
            </div>
          ) : null}
        </header>

        <section
          className="space-y-4 rounded-lg border border-border bg-surface p-4"
          aria-labelledby="screener-filters"
        >
          <h2 id="screener-filters" className="sr-only">
            Filters
          </h2>
          {screens.data ? (
            <SavedScreens
              screens={screens.data}
              current={current}
              draft={draft}
              onLoad={load}
              onError={setError}
            />
          ) : null}

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              run();
            }}
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <div className="space-y-1">
                <label htmlFor="screen-window" className="text-xs font-medium text-text-muted">
                  Return window
                </label>
                <select
                  id="screen-window"
                  value={draft.window}
                  onChange={(event) => {
                    const window = SCAN_WINDOWS.find((item) => item === event.target.value);
                    if (window) setDraft((state) => ({ ...state, window }));
                  }}
                  className="h-9 w-full rounded border border-border bg-bg px-2 text-sm text-text"
                >
                  {SCAN_WINDOWS.map((window) => (
                    <option key={window} value={window}>
                      {window}
                    </option>
                  ))}
                </select>
              </div>
              {NUMBER_FIELDS.map(({ field, label, step }) => (
                <div key={field} className="space-y-1">
                  <label
                    htmlFor={`screen-${field}`}
                    className="text-xs font-medium text-text-muted"
                  >
                    {label}
                  </label>
                  <input
                    id={`screen-${field}`}
                    type="number"
                    inputMode="decimal"
                    step={step}
                    placeholder="Any"
                    value={draft.numbers[field]}
                    onChange={(event) => setNumber(field, event.target.value)}
                    className="h-9 w-full rounded border border-border bg-bg px-2 text-sm text-text"
                  />
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              {(
                [
                  ['aboveSma50', 'Above the 50-day average'],
                  ['tightOnly', 'Tight coil only'],
                  ['excludeBiotech', 'Exclude biotech'],
                ] as const
              ).map(([field, label]) => (
                <label key={field} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft[field]}
                    onChange={(event) =>
                      setDraft((state) => ({ ...state, [field]: event.target.checked }))
                    }
                    className="h-4 w-4"
                  />
                  {label}
                </label>
              ))}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <IndustryPicker
                id="screen-industries"
                label="Only these industries"
                values={draft.industries}
                options={industries}
                onChange={(values) => setDraft((state) => ({ ...state, industries: values }))}
              />
              <IndustryPicker
                id="screen-exclude-industries"
                label="Exclude industries"
                values={draft.excludeIndustries}
                options={industries}
                onChange={(values) =>
                  setDraft((state) => ({ ...state, excludeIndustries: values }))
                }
              />
            </div>

            {error ? (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button type="submit" size="sm">
                <Play className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                Run screen
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setDraft(toDraft(DEFAULT_SCREEN_FILTERS))}
              >
                Clear filters
              </Button>
            </div>
          </form>
        </section>

        <section
          className="rounded-lg border border-border bg-surface"
          aria-labelledby="screen-results"
        >
          <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-4 py-3">
            <div className="min-w-0 flex-1">
              <h2 id="screen-results" className="text-sm font-semibold">
                Results{current ? ` · ${current.name}` : ''}
              </h2>
              {data?.status === 'READY' ? (
                <p className="mt-0.5 font-mono text-xs tabular-nums text-text-muted">
                  {data.total.toLocaleString('en-US')} matches
                  {data.total > SCREEN_RESULT_LIMIT ? ` · showing ${SCREEN_RESULT_LIMIT}` : ''} ·
                  coil checked for {data.coilChecked.toLocaleString('en-US')} leaders
                </p>
              ) : null}
            </div>
            {data?.status === 'READY' && data.sessionDate ? (
              <SymbolExport
                label="screen results"
                fileName={`screen-${(current?.name ?? 'results').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${data.sessionDate}.txt`}
                rows={data.stocks}
              />
            ) : null}
          </div>
          {result.isLoading ? (
            <p className="px-4 py-6 text-sm text-text-muted">Running screen…</p>
          ) : result.isError || !data ? (
            <p className="px-4 py-6 text-sm text-danger">The screen could not be run.</p>
          ) : data.status === 'NOT_AVAILABLE' ? (
            <div className="flex gap-3 p-4 text-sm text-warn">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>
                No stored universe for this session. The screener works on scans from the last three
                months; run the stock leaders scan in{' '}
                <Link to="/settings/market-data" className="underline">
                  Settings → Market data
                </Link>{' '}
                after the close.
              </p>
            </div>
          ) : (
            <StockCandidateList
              candidates={data.stocks}
              horizon={applied.window}
              emptyText="No stock matches these filters."
            />
          )}
        </section>

        <p className="text-xs text-text-muted">
          Tight-coil filters only see stocks whose coil was checked: the leaders under the loosest
          scan thresholds. A shortlist for chart review, not a buy list.
        </p>
      </div>
    </main>
  );
}
